import { useState, useEffect, useMemo } from "react";
import useFetchData from "../hooks/fetch-data.hook";
import useFetch from "../hooks/fetch.hook";
import Field from "./field.component";
import * as Utils from './utils';
import { Form, Row, Col, Button } from 'reactstrap';
import { Player } from "@lottiefiles/react-lottie-player";
import ErrorModal from "../components/errorModal";

/**
 * 
 * @param {import("./types").FormV2Config} config 
 * @param {import("./types").FormV2State} state
 * @param {Record<string, any>} externalState
 * @param {string} [tabToOpen]
 */
const checkTab = (config, state, externalState, tabToOpen) => {
    let firstKey = null;
    if (config && config.tabs && Utils.isObject(config.tabs)) {
        if (tabToOpen) {
            for (const key in config.tabs) {
                if (firstKey) break;
                if (tabToOpen === key) {
                    const tab = config.tabs[key];
                    if (tab && (!tab.condition || tab.condition(state, externalState))) {
                        firstKey = key;
                        break;
                    }
                }
            }
        }
        for (const key in config.tabs) {
            if (firstKey) break;
            const tab = config.tabs[key];
            if (tab && (!tab.condition || tab.condition(state, externalState))) {
                firstKey = key;
                break;
            }
        }
    }

    return firstKey;
}

/**
 * 
 * @param {{
 *   data: import("./types").FormV2FormData;
 *   lang?: string;
 *   fetchTrigger?: number;
 *   isLoadingExternalState?: boolean;
 * }} param0 
 */
const FormV2 = ({
    data,
    lang: _lang,
    fetchTrigger,
    isLoadingExternalState = false
}) => {
    const jsonTabs = data?.config?.tabs ? JSON.stringify(data.config.tabs) : null;
    const lang = useMemo(() => {
        if (_lang) return _lang;
        if (typeof window !== 'undefined') {
            const origin = window.origin;
            if (origin && (
                origin?.indexOf('https://sandbox.curoflow.se') !== -1 ||
                origin?.indexOf('curoflow.gr') !== -1
            )) return 'en';

            const secondaryLanguageDisabled = localStorage?.getItem?.('secondaryLanguageDisabled');
            if (secondaryLanguageDisabled === 'yes') return 'en';
        }

        return 'se';
    }, [_lang, typeof window]);

    const tabs = useMemo(() => data?.config?.tabs, [jsonTabs]);
    const [tab, setTab] = useState(() => checkTab(data.config, data.state, data.externalState));

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const { isInitialLoading, isLoading, data: fetchData, error, refresh } = useFetchData((data?.config?.autoFetch?.url ?? null), {
        method: data?.config?.autoFetch?.method ?? 'get',
        data: data?.config?.autoFetch?.body
    }, fetchTrigger);

    const _fetch = useFetch();

    useEffect(() => {
        if (fetchData) {
            const handler = async (d) => {
                if (data?.config?.onBeforePopulate) {
                    const dt = await data.config.onBeforePopulate(d);
                    if (dt) data.handlePopulate?.(dt);
                } else {
                    data.handlePopulate?.(d);
                }
            }

            handler(fetchData);
        }
    }, [fetchData]);

    useEffect(() => {
        if (!error) setFetchError(null);
        if (!data?.config?.autoFetch?.skipFetchErrorHandling && error) setFetchError(error);
    }, [error]);

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();

        if (!data || !data.handleSubmit || !data.config || (!data.config.onSubmit && !data.config.autoSubmit)) return;

        setIsSubmitting(true);

        try {
            const formData = await data.handleSubmit();
            if (!formData) {
                if (data.config?.id) {
                    const form = document.getElementById(data.config.id);
                    console.log('form element: ', form)
                    if (form) {
                        form.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                    }
                }
                setIsSubmitting(false);
                return;
            }

            if (data.config.onSubmit) {
                await data.config.onSubmit(formData, data.state, data.externalState);
                setIsSubmitting(false);
                return
            }

            if (data.config.autoSubmit) {
                const method = data.config.autoSubmit.method ? (
                    typeof data.config.autoSubmit.method === 'function' ? (
                        data.config.autoSubmit.method(data?.state, data?.externalState)
                    ) : (
                        data.config.autoSubmit.method
                    )
                ) : 'post';
                const url = data.config.autoSubmit.url ? (
                    typeof data.config.autoSubmit.url === 'function' ? (
                        data.config.autoSubmit.url(data?.state, data?.externalState)
                    ) : (
                        data.config.autoSubmit.url
                    )
                ) : null

                const dta = data.config.autoSubmit.onBeforeSubmit ? (await data.config.autoSubmit.onBeforeSubmit(formData, data.state, data.externalState)) : formData;

                console.log('* Form V2: ', '\n* Method: ', method, '\n* URL: ', url, '\n* Data: ', dta);

                if (!url) {
                    setIsSubmitting(false);
                    return;
                }

                const { data: submitData, error: submitError } = await _fetch(method, url, formData);
                if (submitError) {
                    setIsSubmitting(false);
                    if (data.config?.useSubmitErrorForErrorMessage && data.setSubmitError) {
                        data.setSubmitError(submitError);
                        return;
                    }
                    if (!data.config.autoSubmit.skipFetchErrorHandling) setFetchError(data.config.autoSubmit.errorMessage ?? submitError);
                    return;
                }

                if (data.config.autoSubmit.onAfterSubmit) {
                    data.config.autoSubmit.onAfterSubmit(submitData, data.externalState);
                }

                let skipResetOnSuccess = false;
                if (data.config.autoSubmit.skipResetOnSuccess) {
                    if (typeof data.config.autoSubmit.skipResetOnSuccess === 'function') skipResetOnSuccess = data.config.autoSubmit.skipResetOnSuccess(submitData, data.externalState);
                    else if (typeof data.config.autoSubmit.skipResetOnSuccess === 'boolean') skipResetOnSuccess = data.config.autoSubmit.skipResetOnSuccess;
                }

                if (!skipResetOnSuccess) {
                    data.handleReset?.();
                    refresh();
                }

                if (data.config.autoSubmit.successMessage) {
                    const msg = typeof data.config.autoSubmit.successMessage === 'function' ? data.config.autoSubmit.successMessage(submitData, data.externalState) : data.config.autoSubmit.successMessage;
                    if (msg) setFetchError(msg);
                }
            }
        } catch (err) { console.log('form err: ', err) }

        setIsSubmitting(false);
    }

    const CustomButtons = data?.config?.buttons?.custom;

    const submitButton = useMemo(() => {
        const b = data?.config?.buttons?.submit;

        const text = b?.text ? (
            typeof b.text === 'function' ? (
                b.text(data.state, data.externalState)
            ) : (
                b.text
            )
        ) : (
            'Save'
        );

        const isHidden = (b?.isHidden === true || (typeof b?.isHidden === 'function' && b?.isHidden(data.state, data.externalState)) || (tab && tabs && (tabs?.[tab]?.hideButtons === true || (typeof tabs?.[tab]?.hideButtons === 'function' && tabs?.[tab]?.hideButtons(data.state, data.externalState))))) ? true : false;

        const color = b?.color ? (
            typeof b.color === 'function' ? (
                b.color(data.state, data.externalState)
            ) : (
                b.color
            )
        ) : (
            'primary'
        );

        return {
            text,
            isShown: !isHidden,
            color,
            className: data?.config?.classNames?.submitButton,
            style: data?.config?.styles?.submitButton,
        }
    }, [data, tabs, tab]);
    const CustomSubmitButton = data?.config?.buttons?.submit?.custom;

    const resetButton = useMemo(() => {
        const b = data?.config?.buttons?.reset;

        const text = b?.text ? (
            typeof b.text === 'function' ? (
                b.text(data.state, data.externalState)
            ) : (
                b.text
            )
        ) : (
            'Reset'
        );

        const isShown = ((b?.isShown === true || (typeof b?.isShown === 'function' && b?.isShown(data.state, data.externalState))) && (!tabs || (tab && tabs && (!tabs?.[tab]?.hideButtons || (typeof tabs?.[tab]?.hideButtons === 'function' && !tabs?.[tab]?.hideButtons(data.state, data.externalState)))))) ? true : false;

        const color = b?.color ? (
            typeof b.color === 'function' ? (
                b.color(data.state, data.externalState)
            ) : (
                b.color
            )
        ) : (
            'primary'
        );

        return {
            text,
            isShown,
            color,
            className: data?.config?.classNames?.resetButton,
            style: data?.config?.styles?.resetButton,
            callback: b?.callback
        }
    }, [data, tabs, tab]);
    const CustomResetButton = data?.config?.buttons?.reset?.custom;

    const _error = useMemo(() => {
        if (data?.submitError) {
            if (Array.isArray(data?.submitError)) {
                const err = data.submitError.map(e => e.translate(lang)).join(' ');
                return err;
            }

            return data.submitError.translate(lang);
        }

        return null;
    }, [data?.submitError, lang]);

    const _fetchError = useMemo(() => {
        if (fetchError) {
            if (Array.isArray(fetchError)) {
                const err = fetchError.map(e => e.translate(lang)).join(' ');
                return err;
            }

            return fetchError.translate(lang);
        }

        return null;
    }, [fetchError, lang]);

    if (isInitialLoading || isLoadingExternalState) return <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%'
    }}>
        <Player
            autoplay={true}
            loop={true}
            src="/lf30_editor_l5cxzdyf.json"
            style={{ height: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px", width: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px" }}
        ></Player>
    </div>

    return <>
        <Form
            className={`formV2 ${data?.config?.classNames?.form ?? ''}`}
            onSubmit={handleSubmit}
            id={data?.config?.id}
            key={data?.config?.id}
            autoComplete={data?.config?.autoComplete ? 'on' : 'off'}
            style={data?.config?.styles?.form}
            autoSave="off"
        >
            {data?.config?.title ? <div
                className={`formV2-header ${data?.config?.classNames?.header ?? ''}`}
                style={data?.config?.styles?.header}
            >
                <h3>{data.config.title.translate(lang)}</h3>
                {data.config.subtitle ? <p>
                    {data.config.subtitle.translate(lang)}
                </p> : null}
            </div> : null}

            <div
                className={`formV2-main ${data?.config?.classNames?.main ?? ''}`}
                style={data?.config?.styles?.main}
            >
                {tabs && tab ? (
                    <>
                        <Tabs
                            tabs={tabs}
                            tab={tab}
                            onChange={setTab}
                            className={data?.config?.classNames?.tabs}
                            style={data?.config?.styles?.tabs}
                            errors={data.errors?.tabs}
                            state={data?.state}
                            externalState={data?.externalState}
                            lang={lang}
                        />

                        {data?.config?.sections && Utils.isNotEmpty(data.config.sections) ? (
                            <Sections
                                data={data}
                                lang={lang}
                                tab={tab}
                                refresh={refresh}
                                isSubmitting={isSubmitting}
                            />
                        ) : (
                            <Fields
                                key='fields1'
                                data={data}
                                lang={lang}
                                tab={tab}
                                refresh={refresh}
                                isSubmitting={isSubmitting}
                            />
                        )}
                    </>
                ) : (
                    data?.config?.sections && Utils.isNotEmpty(data.config.sections) ? (
                        <Sections
                            data={data}
                            lang={lang}
                            refresh={refresh}
                            isSubmitting={isSubmitting}
                        />
                    ) : (
                        <Fields
                            key='fields2'
                            data={data}
                            lang={lang}
                            refresh={refresh}
                            isSubmitting={isSubmitting}
                        />
                    )
                )}
            </div>

            <div
                className={`formV2-footer ${data?.config?.classNames?.footer ?? ''}`}
                style={data?.config?.styles?.footer}
            >
                {_error ? <p className={`formV2-footer-error ${data?.config?.classNames?.submitError ?? ''}`} style={data?.config?.styles?.submitError}>
                    {_error}
                </p> : null}
                {CustomButtons ? (
                    <CustomButtons
                        data={data}
                        lang={lang}
                        isSubmitting={isSubmitting}
                        isInitialLoading={isInitialLoading}
                        isLoading={isLoading}
                        tab={tab}
                    />
                ) : <div
                    className={`formV2-footer-buttons ${data?.config?.classNames?.buttonsContainer ?? ''}`}
                    style={data?.config?.styles?.buttonsContainer}
                >
                    {data?.config?.buttons?.others?.map?.((btn) => {
                        if (!btn || !btn.text || !btn.onClick) return null;
                        if (btn.condition && !btn.condition(data.state, data.externalState)) return null;

                        const color = typeof btn.color === 'function' ? btn.color(data.state, data.externalState) : (btn.color ?? 'primary');

                        return <Button
                            key={btn.text}
                            type='button'
                            onClick={e => btn.onClick?.(e, data.state, data.externalState)}
                            color={color}
                            className={btn.className}
                            style={btn.style}
                            disabled={isSubmitting}
                        >
                            {btn.text.translate(lang)}
                        </Button>
                    })}

                    {CustomResetButton ? (
                        <CustomResetButton
                            data={data}
                            lang={lang}
                            isSubmitting={isSubmitting}
                            isInitialLoading={isInitialLoading}
                            isLoading={isLoading}
                            tab={tab}
                        />
                    ) : resetButton?.isShown ? <Button
                        key='reset-button'
                        type='button'
                        onClick={e => {
                            data.handleReset?.();
                            resetButton.callback?.(e, data.externalState);
                        }}
                        color={resetButton.color}
                        className={resetButton.className}
                        style={resetButton.style}
                        disabled={isSubmitting}
                    >
                        {resetButton.text.translate(lang)}
                    </Button> : null}

                    {CustomSubmitButton ? (
                        <CustomSubmitButton
                            data={data}
                            lang={lang}
                            isSubmitting={isSubmitting}
                            isInitialLoading={isInitialLoading}
                            isLoading={isLoading}
                            tab={tab}
                        />
                    ) : submitButton?.isShown ? <Button
                        key='submit-button'
                        type='submit'
                        color={submitButton.color}
                        className={submitButton.className}
                        style={submitButton.style}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving'.translate(lang) : submitButton.text.translate(lang)}
                    </Button> : null}
                </div>}
            </div>

            {isLoading && !isInitialLoading ? <div className="loader-wrap" >
                <Player
                    autoplay={true}
                    loop={true}
                    src="/lf30_editor_l5cxzdyf.json"
                    style={{ height: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px", width: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px" }}
                ></Player>
            </div> : null}
        </Form>

        {_fetchError ? <ErrorModal
            isOpen={_fetchError ? true : false}
            size="md"
            lang={lang}
            toggle={() => setFetchError(null)}
        >
            {_fetchError}
        </ErrorModal> : null}
    </>
}

export default FormV2;

/**
 * 
 * @param {{
 *   data: import("./types").FormV2FormData;
 *   lang: string;
 *   tab?: string;
 *   refresh: () => void;
 *   isSubmitting?: boolean;
 * }} param0 
*/
const Sections = ({
    data,
    lang,
    tab = null,
    refresh,
    isSubmitting,
}) => {
    return <Row>
        {Object.keys(data.config.sections).map(sectionName => {
            const section = data.config.sections[sectionName];

            if (!section || (section.condition && !section.condition(data.state, data.externalState))) return null;
            if (tab && (!section.tab || section.tab !== tab)) return null;

            const cols = Utils.getCols(section.cols ?? null, data.state, data.externalState);

            return <Col
                key={section.id ?? sectionName}
                id={section.id}
                {...cols}
                className={section.className}
                style={section.style}
            >
                {section.title ? <div className='formV2-header'>
                    <h4>{section.title.translate(lang)}</h4>
                    {section.subtitle ? <p>{section.subtitle.translate(lang)}</p> : null}
                </div> : null}

                {section.subsections && Utils.isNotEmpty(section.subsections) ? (
                    <Row>
                        {Object.keys(section.subsections).map(subsectionName => {
                            const subsection = section.subsections[subsectionName];

                            if (!subsection || (subsection.condition && !subsection.condition(data.state, data.externalState))) return null;

                            const cols = Utils.getCols(subsection.cols ?? null, data.state, data.externalState);

                            return <Col
                                key={subsection.id ?? subsectionName}
                                id={subsection.id}
                                {...cols}
                                className={subsection.className}
                                style={subsection.style}
                            >
                                {subsection.title ? <div className='formV2-header'>
                                    <h5>{subsection.title.translate(lang)}</h5>
                                    {subsection.subtitle ? <p>{subsection.subtitle.translate(lang)}</p> : null}
                                </div> : null}

                                <Fields
                                    data={data}
                                    lang={lang}
                                    subsection={subsectionName}
                                    refresh={refresh}
                                    isSubmitting={isSubmitting}
                                />
                            </Col>
                        })}
                    </Row>
                ) : (
                    <Fields
                        data={data}
                        lang={lang}
                        section={sectionName}
                        refresh={refresh}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Col>
        })}
    </Row>
}

/**
 * 
 * @param {{
 *   data: import("./types").FormV2FormData;
 *   lang: string;
 *   tab?: string;
 *   section?: string;
 *   subsection?: string;
 *   refresh: () => void;
 *   isSubmitting?: boolean;
* }} param0 
*/
const Fields = ({
    data,
    lang,
    tab = null,
    section = null,
    subsection = null,
    refresh,
    isSubmitting,
}) => {
    return <Row>
        {Object.keys(data.fields).map(key => {
            const field = data.fields[key];

            if (!field || (field.condition && !field.condition(data.state, data.externalState))) return null;
            if (!data.state?.[key]) return null;

            if (tab && (!field.tab || field.tab !== tab)) return null;
            if (section && (!field.section || field.section !== section)) return null;
            if (subsection && (!field.subsection || field.subsection !== subsection)) return null;

            return <Field
                key={field.id ?? key}
                data={data}
                field={field}
                fieldName={key}
                refresh={refresh}
                lang={lang}
                isSubmitting={isSubmitting}
            />
        })}
    </Row>
}

/**
 * 
 * @param {{
 *   tabs: import("./types").FormV2Config['tabs'];
 *   tab: string;
 *   onChange: (tab: string) => void;
 *   className?: string;
 *   style?: React.CSSProperties;
 *   state: import("./types").FormV2State;
 *   externalState: Record<string, any>;
 *   lang: string;
 *   errors?: string[];
 *   sideComponent?: React.ReactNode;
 * }} param0 
 * @returns 
 */
export const Tabs = ({
    tabs,
    tab,
    onChange,
    className,
    style,
    state,
    externalState,
    lang,
    errors,
    sideComponent,
}) => {
    const activeTab = tabs?.[tab];
    if (!activeTab) return null;

    return <div
        className={`formV2-tabs-container ${className ?? ''}`}
        style={style}
    >
        <div className="formV2-tabs">
            {Object.keys(tabs).map(key => {
                const t = tabs?.[key];
                if (!t || !t.label) return null;

                if (t.condition && !t.condition(state, externalState)) return null;

                const isDisabled = t.isDisabled ? t.isDisabled(state, externalState) : false;
                const isActive = key === tab;
                const hasError = errors?.includes(key);

                return <Button
                    key={t.id ?? key}
                    id={t.id}
                    type='button'
                    color='primary'
                    onClick={() => !isDisabled ? onChange(key) : null}
                    className={`formV2-tab ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''} ${hasError ? 'error' : ''}`}
                    disabled={isDisabled}
                >
                    {t.label.translate(lang)}
                </Button>
            })}
            {sideComponent}
        </div>

        {activeTab?.title ? <div className="formV2-header">
            <h4>
                {activeTab.title.translate(lang)}
            </h4>
        </div> : null}
    </div>
}